import { createAction } from "@reduxjs/toolkit";
import {
  AUTHENTICATE_USER,
  AUTHENTICATING_USER_FAILED,
  AUTHENTICATING_USER_SUCCEEDED,
  FETCH_USER_PROFILE,
  FETCHING_USER_PROFILE_FAILED,
  FETCHING_USER_PROFILE_SUCCEDDED,
  UPDATING_USER_PROFILE,
  UPDATING_USER_PROFILE_SUCCEDDED,
  UPDATING_USER_PROFILE_FAILED,
  CHANGING_PASSWORD,
  CHANGING_PASSWORD_SUCCEEDED,
  CHANGING_PASSWORD_FAILED,
  UPDATING_EMAIL_SIGNATURE,
  UPDATING_EMAIL_SIGNATURE_SUCCEEDED,
  UPDATING_EMAIL_SIGNATURE_FAILED,
  FETCHING_ACTIVE_USERS_COUNT,
  FETCHING_ACTIVE_USERS_COUNT_SUCCEEDED,
  FETCHING_ACTIVE_USERS_COUNT_FAILED,
  FETCHING_USERS,
  FETCHING_USERS_SUCCEEDED,
  FETCHING_USERS_FAILED,
  LOGOUT_USER,
  FETCH_SSO_URL_REQUEST,
  FETCHING_SSO_URL_SUCCEEDED,
  FETCHING_SSO_URL_FAILED,
  SSO_AUTHENTICATION_FAILED,
  FORGOT_PASSWORD_REQUEST,
  FORGOT_PASSWORD_SUCCEEDED,
  FORGOT_PASSWORD_FAILED,
  SIGNUP_USER_REQUEST,
  SIGNUP_USER_SUCCEEDED,
  SIGNUP_USER_FAILED,
  USER_ACTIVATION_REQUEST,
  USER_ACTIVATION_SUCCEEDED,
  USER_ACTIVATION_FAILED,
  VALIDATE_REGISTRATION,
  VALIDATING_REGISTRATION_SUCCEEDED,
  VALIDATING_REGISTRATION_FAILED,
  VALIDATING_PASSWORD_RESET_LINK,
  VALIDATING_PASSWORD_RESET_LINK_SUCCEEDED,
  VALIDATING_PASSWORD_RESET_LINK_FAILED,
  FETCH_USER_INVITATION,
  FETCHING_USER_INVITATION_SUCCEEDED,
  FETCHING_USER_INVITATION_FAILED,
  REGISTER_INVITED_USER,
  REGISTERING_INVITED_USER_SUCCEEDED,
  REGISTERING_INVITED_USER_FAILED,
  INVALIDATE_FORGOT_PASSWORD,
  INVALIDATE_USER_NOTIFICATION_PREFERENCES_UPDATE,
  FETCHING_USER_NOTIFICATION_PREFERENCES,
  FETCHING_USER_NOTIFICATION_PREFERENCES_FAILED,
  FETCHING_USER_NOTIFICATION_PREFERENCES_SUCCEEDED,
  UPDATING_USER_NOTIFICATION_PREFERENCES,
  UPDATING_USER_NOTIFICATION_PREFERENCES_SUCCEEDED,
  UPDATING_USER_NOTIFICATION_PREFERENCES_FAILED,
  RESET_PASSWORD,
  RESETTING_PASSWORD_SUCCEEDED,
  RESETTING_PASSWORD_FAILED,
} from "./actionTypes";

import axios from "axios";
import { takeEvery, call, put } from "redux-saga/effects";
import {
  ResetPasswordRequest,
  UserActivationError,
  UserActivationRequest,
  UserActivationResponse,
  UserProfile,
  UserRegistrationValidationError,
  UserRegistrationValidationRequest,
  UserSSO,
  UserSSOError,
  UserSSORequest,
  ValidatePasswordResetRequest,
} from "../../types/users";
import buildUrl from "build-url-ts";
import AuthService from "src/utils/auth.service";

// eslint-disable-next-line no-undef
const apiUrl = `${process.env.REACT_APP_API_URL}`;
const usersApiUrl = `${process.env.REACT_APP_USERS_API_URL}`;
const LOGIN_URL = `${usersApiUrl}/users/3.0/oauth/token`;
const AUTHORIZE_URL = `${usersApiUrl}/users/3.0/oauth/token/refresh`;
const SSO_URL = `${apiUrl}/api/2.0/users/sso/initiate`;
const FORGOT_PASSWORD_URL = `${usersApiUrl}/users/3.0/forgot-password`;
const USERS_BASE_URL = `${usersApiUrl}/users/3.0`;
const REGISTRATION_URL = `${usersApiUrl}/users/3.0/registrations`;
const INVITATION_BASE_URL = `${usersApiUrl}/users/3.0/invitations`;

interface AuthenticationPayload {
  username: string;
  password: string;
}

interface AuthenticationSucceeded {
  access_token: string;
  refresh_token: string;
  type: string;
  expiresIn: number;
}

interface AuthenticatingUserFailed {
  errors: [
    {
      code: string;
      message: string;
    },
  ];
}

export const authenticateUser =
  createAction<AuthenticationPayload>(AUTHENTICATE_USER);

export const authenticatingUserSucceeded = createAction<AuthenticationSucceeded>(
  AUTHENTICATING_USER_SUCCEEDED,
);

const authenticatingUserFailed = createAction<AuthenticatingUserFailed>(AUTHENTICATING_USER_FAILED);

const authenticationUserSaga = function* (
  action: ReturnType<typeof authenticateUser>,
): any {
  const { username, password } = action.payload;

  const data = {
    username: username,
    password: password,
  };

  const authenticateUserAPI = () => {
    return axios({
      method: "POST",
      url: LOGIN_URL,
      data,
    }).then((response) => {
      return response.data;
    });
  };

  try {
    const response = yield call(authenticateUserAPI);
    yield put(authenticatingUserSucceeded(response));
  } catch (errors) {
    yield put(authenticatingUserFailed({ errors: errors } as AuthenticatingUserFailed));
  }
};

/**
 * Refresh token
 */
export function refreshToken(refresh_token: string) {
  const data = {
    refresh_token,
  };

  return axios({
    method: "POST",
    url: AUTHORIZE_URL,
    data,
  });
}

/**
 * Fetch user profile
 */
export const fetchUserProfile = createAction<void>(FETCH_USER_PROFILE);
const fetchingUserProfileSucceeded = createAction<UserProfile>(
  FETCHING_USER_PROFILE_SUCCEDDED,
);
const fetchingUserProfileFailed = createAction<any>(
  FETCHING_USER_PROFILE_FAILED,
);

const fetchUserProfileSaga = function* (): any {
  const url = buildUrl(usersApiUrl, {
    path: `/users/3.0/me`,
  });

  const fetchUserProfileApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchUserProfileApi);
    yield put(fetchingUserProfileSucceeded(response));
  } catch (error) {
    yield put(fetchingUserProfileFailed(error));
  }
};

export const fetchSSOUrlFromEmail = createAction<UserSSORequest>(
  FETCH_SSO_URL_REQUEST,
);
export const fetchingSSOUrlFromEmailSucceeded = createAction<UserSSO>(
  FETCHING_SSO_URL_SUCCEEDED,
);
export const fetchingSSOUrlFromEmailFailed = createAction<UserSSOError>(
  FETCHING_SSO_URL_FAILED,
);

const fetchSSOUrlFromEmailSaga = function* (
  action: ReturnType<typeof fetchSSOUrlFromEmail>,
): any {
  const url = buildUrl(SSO_URL, {
    queryParams: {
      email: action.payload.email,
    },
  });

  const fetchSSOUrlFromEmailApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchSSOUrlFromEmailApi);
    yield put(fetchingSSOUrlFromEmailSucceeded(response));
  } catch (error: any) {
    yield put(fetchingSSOUrlFromEmailFailed(error));
  }
};

export const ssoAuthenticationFailed = createAction(
  SSO_AUTHENTICATION_FAILED,
  (reason: any) => {
    return { payload: { reason } };
  },
);

export const fetchUserNotifications = createAction<void>(
  FETCHING_USER_NOTIFICATION_PREFERENCES,
);
const fetchingUserNotificationsSucceeded = createAction<any>(
  FETCHING_USER_NOTIFICATION_PREFERENCES_SUCCEEDED,
);
const fetchingUserNotificationsFailed = createAction<any>(
  FETCHING_USER_NOTIFICATION_PREFERENCES_FAILED,
);

const fetchUserNotificationsSaga = function* (): any {
  const url = buildUrl(usersApiUrl, {
    path: `/users/3.0/preferences/notifications`,
  });

  const fetchUserNotificationsApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchUserNotificationsApi);
    yield put(fetchingUserNotificationsSucceeded(response));
  } catch (error) {
    yield put(fetchingUserNotificationsFailed(error));
  }
};

export const updateUserNotification = createAction<any>(
  UPDATING_USER_NOTIFICATION_PREFERENCES,
);
const updatingUserNotificationSucceeded = createAction<any>(
  UPDATING_USER_NOTIFICATION_PREFERENCES_SUCCEEDED,
);
const updatingUserNotificationFailed = createAction<any>(
  UPDATING_USER_NOTIFICATION_PREFERENCES_FAILED,
);

const updateUserNotificationSaga = function* (
  action: ReturnType<typeof updateUserNotification>,
): any {
  const url = buildUrl(usersApiUrl, {
    path: `/users/3.0/preferences/notifications`,
  });

  const updateUserNotificationApi = () => {
    return axios({
      method: "PUT",
      url: url,
      data: action.payload,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(updateUserNotificationApi);
    yield put(updatingUserNotificationSucceeded(response));
  } catch (error) {
    yield put(updatingUserNotificationFailed(error));
  }
};

export const updateUserProfile = createAction<any>(UPDATING_USER_PROFILE);
export const updatingUserProfileSucceeded = createAction<any>(
  UPDATING_USER_PROFILE_SUCCEDDED,
);
export const updatingUserProfileFailed = createAction<any>(
  UPDATING_USER_PROFILE_FAILED,
);

const updatingUserProfileSaga = function* (
  action: ReturnType<typeof updateUserProfile>,
): any {
  const url = buildUrl(usersApiUrl, {
    path: `/users/3.0/me`,
  });

  const updateUserProfileApi = () => {
    return axios({
      method: "PUT",
      url: url,
      data: action.payload,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(updateUserProfileApi);
    yield put(updatingUserProfileSucceeded(response));
  } catch (error) {
    yield put(updatingUserProfileFailed(error));
  }
};

export const changePassword = createAction<{
  oldPassword: string;
  newPassword1: string;
  newPassword2: string;
}>(CHANGING_PASSWORD);
export const changingPasswordSucceeded = createAction<any>(
  CHANGING_PASSWORD_SUCCEEDED,
);
const changingPasswordFailed = createAction<any>(CHANGING_PASSWORD_FAILED);

const changePasswordSaga = function* (
  action: ReturnType<typeof changePassword>,
): any {
  const data = action.payload;
  const url = buildUrl(usersApiUrl, {
    path: `/users/3.0/change-password`,
  });
  const changePasswordApi = () => {
    return axios({
      method: "PUT",
      url: url,
      data,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(changePasswordApi);
    yield put(changingPasswordSucceeded(response));
  } catch (error) {
    yield put(changingPasswordFailed(error));
  }
};

export const updateEmailSignature = createAction<any>(UPDATING_EMAIL_SIGNATURE);
const updatingEmailSignatureSucceeded = createAction<any>(
  UPDATING_EMAIL_SIGNATURE_SUCCEEDED,
);
const updatingEmailSignatureFailed = createAction<any>(
  UPDATING_EMAIL_SIGNATURE_FAILED,
);

const updateEmailSignatureSaga = function* (
  action: ReturnType<typeof updateEmailSignature>,
): any {
  const url = buildUrl(usersApiUrl, {
    path: `/users/3.0/email-signature`,
  });

  const updateEmailSignatureApi = () => {
    return axios({
      method: "PUT",
      url: url,
      data: action.payload,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(updateEmailSignatureApi);
    yield put(updatingEmailSignatureSucceeded(response));
  } catch (error) {
    yield put(updatingEmailSignatureFailed(error));
  }
};

//fetch active users for account
export const fetchActiveUsersCountForAccount = createAction<any>(
  FETCHING_ACTIVE_USERS_COUNT,
);
const fetchingActiveUsersCountForAccountSucceeded = createAction<any>(
  FETCHING_ACTIVE_USERS_COUNT_SUCCEEDED,
);
const fetchingActiveUsersCountForAccountFailed = createAction<any>(
  FETCHING_ACTIVE_USERS_COUNT_FAILED,
);

const fetchActiveUsersCountForAccountSaga = function* (
  action: ReturnType<typeof fetchActiveUsersCountForAccount>,
): any {
  const url = buildUrl(apiUrl, {
    path: `/api/2.0/users/active/count`,
  });

  const fetchActiveUsersCountForAccountApi = () => {
    return axios({
      method: "GET",
      url: url,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchActiveUsersCountForAccountApi);
    yield put(fetchingActiveUsersCountForAccountSucceeded(response));
  } catch (error) {
    yield put(fetchingActiveUsersCountForAccountFailed(error));
  }
};

//fetch users
export const fetchUsers = createAction<{
  query: string;
  page: string;
  ignoreCurrentUser: boolean;
}>(FETCHING_USERS);
const fetchingUsersSucceeded = createAction<any>(FETCHING_USERS_SUCCEEDED);
const fetchingUsersFailed = createAction<any>(FETCHING_USERS_FAILED);

const fetchUsersSaga = function* (
  action: ReturnType<typeof fetchActiveUsersCountForAccount>,
): any {
  const url = buildUrl(apiUrl, {
    path: `/api/2.0/search/users/${action.payload.page}`,
  });

  const params = {
    query: action.payload.query,
    limit: 25,
    ignoreMe: action.payload.ignoreMe,
  };

  const fetchUsersApi = () => {
    return axios({
      method: "GET",
      url: url,
      params: params,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchUsersApi);
    yield put(fetchingUsersSucceeded(response));
  } catch (error) {
    yield put(fetchingUsersFailed(error));
  }
};

export const logoutUser = createAction<void>(LOGOUT_USER);

const logoutUserSaga = function* (): any {
  if (AuthService.isAuthenticated()) {
    const logoutUserApi = () => {
      return axios({
        method: "DELTE",
        url: `${apiUrl}/api/2.0/users/logout`,
      }).then((response) => response.data);
    };

    try {
      yield call(logoutUserApi);
      AuthService.clearAccessToken();
    } catch (error) {
      console.log("error", error);
    }
  }
};

export const forgotPassword = createAction<{ email: string }>(
  FORGOT_PASSWORD_REQUEST,
);
export const invalidateForgotPassword = createAction(
  INVALIDATE_FORGOT_PASSWORD,
);

const forgotPasswordSucceeded = createAction<any>(FORGOT_PASSWORD_SUCCEEDED);
const forgotPasswordFailed = createAction<any>(FORGOT_PASSWORD_FAILED);

const forgotPasswordSaga = function* (
  action: ReturnType<typeof forgotPassword>,
): any {
  const forgotPasswordApi = () => {
    return axios({
      method: "POST",
      url: FORGOT_PASSWORD_URL,
      params: {
        email: action.payload.email,
      },
    }).then((response) => response.data);
  };

  try {
    const response = yield call(forgotPasswordApi);
    yield put(forgotPasswordSucceeded(response));
  } catch (error) {
    yield put(forgotPasswordFailed(error));
  }
};

export const signUpUser = createAction<{ email: string }>(SIGNUP_USER_REQUEST);
const signUpUserSucceeded = createAction<any>(SIGNUP_USER_SUCCEEDED);
const signUpUserFailed = createAction<any>(SIGNUP_USER_FAILED);

const signUpUserSaga = function* (action: ReturnType<typeof signUpUser>): any {
  const signUpUserApi = () => {
    return axios({
      method: "POST",
      url: REGISTRATION_URL,
      data: {
        email: action.payload.email,
      },
    }).then((response) => response.data);
  };

  try {
    const response = yield call(signUpUserApi);
    yield put(signUpUserSucceeded(response));
  } catch (error) {
    yield put(signUpUserFailed(error));
  }
};

export const validateRegistration = createAction<UserRegistrationValidationRequest>(
  VALIDATE_REGISTRATION,
);

const validateRegistrationSucceeded = createAction<void>(
  VALIDATING_REGISTRATION_SUCCEEDED,
);
const validateRegistrationFailed = createAction<UserRegistrationValidationError>(
  VALIDATING_REGISTRATION_FAILED,
);

const validateRegistrationSaga = function* (
  action: ReturnType<typeof validateRegistration>,
): any {
  const { id, activationKey } = action.payload;

  const url = buildUrl(REGISTRATION_URL, {
    path: `${id}/validate`,
    queryParams: {
      activation_token: activationKey,
    },
  });

  const validateRegistrationApi = () => {
    return axios({
      method: "POST",
      url: url,
    }).then((response) => response.data);
  };
  try {
    yield call(validateRegistrationApi);
    yield put(validateRegistrationSucceeded());
  } catch (errors) {
    yield put(validateRegistrationFailed(errors as UserRegistrationValidationError));
  }
};

export const validatePasswordReset = createAction<ValidatePasswordResetRequest>(
  VALIDATING_PASSWORD_RESET_LINK,
);

const validatePasswordResetSucceeded = createAction<boolean>(
  VALIDATING_PASSWORD_RESET_LINK_SUCCEEDED,
);
const validatePasswordResetFailed = createAction<boolean>(
  VALIDATING_PASSWORD_RESET_LINK_FAILED,
);

const validatePasswordResetSaga = function* (
  action: ReturnType<typeof validatePasswordReset>,
): any {
  const { id, key } = action.payload;

  const url = buildUrl(USERS_BASE_URL, {
    path: `${id}/reset-password/validate`,
  });

  const validatePasswordResetApi = () => {
    return axios({
      method: "POST",
      url: url,
      params: {
        recoveryKey: key,
      },
    }).then((response) => response.data);
  };
  try {
    const response = yield call(validatePasswordResetApi);
    yield put(validatePasswordResetSucceeded(response));
  } catch (error) {
    yield put(validatePasswordResetFailed(error as boolean));
  }
};

export const userActivation = createAction<UserActivationRequest>(
  USER_ACTIVATION_REQUEST,
);

const userActivationSucceeded = createAction<UserActivationResponse>(
  USER_ACTIVATION_SUCCEEDED,
);
const userActivationFailed = createAction<UserActivationError>(
  USER_ACTIVATION_FAILED,
);

const userActivationSaga = function* (
  action: ReturnType<typeof userActivation>,
): any {
  const { id, activationKey, user } = action.payload;

  const url = buildUrl(REGISTRATION_URL, {
    path: `${id}/activate`,
    queryParams: {
      activation_token: activationKey,
    },
  });

  const userActivationApi = () => {
    return axios({
      method: "PUT",
      url: url,
      data: user,
    }).then((response) => response.data);
  };
  try {
    const response = yield call(userActivationApi);
    yield put(userActivationSucceeded(response));
  } catch (error) {
    yield put(userActivationFailed(error as UserActivationError));
  }
};

export const resetPassword =
  createAction<ResetPasswordRequest>(RESET_PASSWORD);
const resetPasswordSucceeded = createAction<any>(RESETTING_PASSWORD_SUCCEEDED);
const resetPasswordFailed = createAction<any>(RESETTING_PASSWORD_FAILED);

const resetPasswordSaga = function* (
  action: ReturnType<typeof resetPassword>,
): any {
  const resetPasswordApi = () => {
    const { id, passwordData } = action.payload as ResetPasswordRequest;

    return axios({
      method: "POST",
      url: `${USERS_BASE_URL}/${id}/reset-password`,
      data: passwordData,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(resetPasswordApi);
    yield put(resetPasswordSucceeded(response));
  } catch (error) {
    yield put(resetPasswordFailed(error));
  }
};

export const fetchingUserInvitation = createAction<{ id: string; key: string }>(
  FETCH_USER_INVITATION,
);
const fetchingUserInvitationSucceeded = createAction<any>(
  FETCHING_USER_INVITATION_SUCCEEDED,
);
const fetchingUserInvitationFailed = createAction<any>(
  FETCHING_USER_INVITATION_FAILED,
);

const fetchingUserInvitationSaga = function* (
  action: ReturnType<typeof fetchingUserInvitation>,
): any {
  const { id, key } = action.payload;

  const url = buildUrl(INVITATION_BASE_URL, {
    path: `/${id}/validate`,
  });

  const fetchingUserInvitationApi = () => {
    return axios({
      method: "POST",
      url: url,
      params: {
        token: key,
      },
    }).then((response) => response.data);
  };

  try {
    const response = yield call(fetchingUserInvitationApi);
    yield put(fetchingUserInvitationSucceeded(response));
  } catch (error) {
    yield put(fetchingUserInvitationFailed(error));
  }
};

export const registerInvitedUser = createAction<{
  id: string;
  firstName: string;
  lastName: string;
  password: string;
  email: string;
}>(REGISTER_INVITED_USER);

export const registerInvitedUserSucceeded = createAction<any>(
  REGISTERING_INVITED_USER_SUCCEEDED,
);
const registerInvitedUserFailed = createAction<any>(
  REGISTERING_INVITED_USER_FAILED,
);

const registerInvitedUserSaga = function* (
  action: ReturnType<typeof registerInvitedUser>,
): any {
  const { id, ...rest } = action.payload;

  const user = {
    ...rest,
  };

  const registerInvitedUserApi = () => {
    return axios({
      method: "POST",
      url: `${INVITATION_BASE_URL}/${id}/accept`,
      data: user,
    }).then((response) => response.data);
  };

  try {
    const response = yield call(registerInvitedUserApi);
    yield put(registerInvitedUserSucceeded(response));
  } catch (errors) {
    yield put(registerInvitedUserFailed(errors));
  }
};

export const invalidateNotificationUpdate = createAction<any>(INVALIDATE_USER_NOTIFICATION_PREFERENCES_UPDATE);

export default function* usersSaga() {
  yield takeEvery(AUTHENTICATE_USER, authenticationUserSaga);
  yield takeEvery(FETCH_USER_PROFILE, fetchUserProfileSaga);
  yield takeEvery(FETCHING_USER_NOTIFICATION_PREFERENCES, fetchUserNotificationsSaga);
  yield takeEvery(UPDATING_USER_NOTIFICATION_PREFERENCES, updateUserNotificationSaga);
  yield takeEvery(UPDATING_USER_PROFILE, updatingUserProfileSaga);
  yield takeEvery(CHANGING_PASSWORD, changePasswordSaga);
  yield takeEvery(UPDATING_EMAIL_SIGNATURE, updateEmailSignatureSaga);
  yield takeEvery(
    FETCHING_ACTIVE_USERS_COUNT,
    fetchActiveUsersCountForAccountSaga,
  );
  yield takeEvery(FETCHING_USERS, fetchUsersSaga);
  yield takeEvery(LOGOUT_USER, logoutUserSaga);
  yield takeEvery(FETCH_SSO_URL_REQUEST, fetchSSOUrlFromEmailSaga);
  yield takeEvery(FORGOT_PASSWORD_REQUEST, forgotPasswordSaga);
  yield takeEvery(SIGNUP_USER_REQUEST, signUpUserSaga);
  yield takeEvery(VALIDATE_REGISTRATION, validateRegistrationSaga);
  yield takeEvery(USER_ACTIVATION_REQUEST, userActivationSaga);
  yield takeEvery(VALIDATING_PASSWORD_RESET_LINK, validatePasswordResetSaga);
  yield takeEvery(RESET_PASSWORD, resetPasswordSaga);
  yield takeEvery(FETCH_USER_INVITATION, fetchingUserInvitationSaga);
  yield takeEvery(REGISTER_INVITED_USER, registerInvitedUserSaga);
}
